import { MemoizedSelector, createFeatureSelector, createSelector } from '@ngrx/store'
import { AppState } from '../../core/store'
import { TierNames } from './../../tier/tier.model'
import {
  selectCurrentClient,
  selectCurrentStudy,
  selectCurrentYear,
} from './../../core/store/broker/broker.selectors'
import { selectClientsState, hasCreditPortfolio } from '../../core/store/clients.selectors'
import * as fromCredit from './credit.reducer'
import { CreditTransactionType } from '../model/credit-layer.model'
import { Client } from '../../core/model/client.model'

export const selectCreditState: MemoizedSelector<AppState, fromCredit.State> =
  createFeatureSelector(fromCredit.CREDIT_FEATURE_KEY)

export const selectCreditModuleContext = createSelector(
  selectCreditState,
  (state) => state.moduleContext,
)

export const selectCurrentPortfolio = createSelector(
  selectCreditState,
  (state) => state?.currentPortfolio,
)
export const selectCurrentPortfolioId = createSelector(
  selectCurrentPortfolio,
  (state) => state?._id,
)
export const selectGrossLossScenarios = createSelector(
  selectCurrentPortfolio,
  (state) => state?.gross_loss_scenarios || [],
)

export const selectAllowedGrossLossScenarios = createSelector(selectCreditState, (state) => {
  const allowedGls = state.submission?.currentCreditSubmissionStructure?.selected_gls
  if (allowedGls) {
    const uniqueIds = allowedGls.split(',')
    return state.currentPortfolio?.gross_loss_scenarios?.filter((gls) =>
      uniqueIds.includes(gls._id),
    )
  }
  return state.currentPortfolio?.gross_loss_scenarios
})

export const selectTotalExposure = createSelector(
  selectCurrentPortfolio,
  (state) => state?.total_exposure,
)
export const selectDisableLoanDependentTabs = createSelector(
  selectCurrentPortfolio,
  (state) =>
    !state?.gross_loss_scenarios?.some((scenario) => scenario.loan_gross_loss_scenario_data),
)

export const selectCreditStructures = createSelector(
  selectCreditState,
  (state) => state.creditStructures,
)
export const selectCreditStructureNames = createSelector(selectCreditStructures, (structures) =>
  structures.map((structure) => structure.label),
)
export const selectCheckedCreditStructures = createSelector(selectCreditStructures, (state) =>
  state?.filter((s) => s.checked),
)

export const selectCreditStructureGroups = createSelector(
  selectCreditState,
  (state) => state?.creditStructureGroups,
)
export const selectCheckedCreditStructureGroups = createSelector(
  selectCreditStructureGroups,
  (state) => state?.filter((s) => s.checked),
)

export const selectGseStructures = createSelector(selectCreditState, (state) => state.gseStructures)
export const selectCheckedGseStructures = createSelector(selectGseStructures, (state) =>
  state?.filter((s) => s.checked),
)

export const selectGseStructureGroups = createSelector(
  selectCreditState,
  (state) => state?.gseStructureGroups,
)
export const selectCheckedGseStructureGroups = createSelector(selectGseStructureGroups, (state) =>
  state?.filter((s) => s.checked),
)

export const selectCurrentCalculationStructure = createSelector(
  selectCreditState,
  (state) => state.currentCalculationStructure,
)
export const selectCurrentStructureName = createSelector(
  selectCurrentCalculationStructure,
  (state) => state?.name || '',
)

export const selectCreditTierBreadcrumbs = createSelector(
  selectCurrentClient,
  selectCurrentYear,
  selectCurrentStudy,
  selectCurrentCalculationStructure,
  (client, year, program, structure): TierNames => {
    return {
      client: client?.name,
      year: year?.year,
      program: program?.name,
      structure: structure?.name,
    }
  },
)

export const selectTransactions = createSelector(selectCurrentCalculationStructure, (state) => {
  return state?.transactions
})

/**
 * Returns transactions with QS single-tranche mapping applied.
 * QS transactions get a single tranche with _id set to the transaction _id.
 */
export const selectAllTransactions = createSelector(selectTransactions, (transactions) => {
  if (!transactions) {
    return []
  }
  return transactions.map((transaction) => {
    if (transaction.transaction_type === CreditTransactionType.pmiQs) {
      return {
        ...transaction,
        tranches: [
          {
            ...transaction.tranches[0],
            _id: transaction._id,
          },
        ],
      }
    }
    return transaction
  })
})

export const selectCurrentTransaction = createSelector(
  selectCreditState,
  (state) => state?.currentTransaction,
)

export const selectSelectedDefaultScenario = createSelector(
  selectCurrentCalculationStructure,
  (state) => state?.selected_default_scenario,
)

export const selectSelectedGrossScenarios = createSelector(
  selectCurrentCalculationStructure,
  (state) => state?.selected_scenario,
)

export const selectSelectedReturnPeriod = createSelector(
  selectCurrentCalculationStructure,
  (state) => state?.selected_return_period,
)

export const selectSelectedDefaultReturnPeriod = createSelector(
  selectCurrentCalculationStructure,
  (state) => state?.selected_default_return_period,
)

export const selectCreditResults = createSelector(
  selectCreditState,
  (state) => state?.creditResults,
)

export const selectDefaultScenarioResults = createSelector(
  selectCreditState,
  (state) => state?.defaultScenarioResults,
)

export const selectPortfolioMetricsScenarios = createSelector(
  selectCreditState,
  (state) => state?.portfolioMetricsScenarios,
)

export const selectTowerControls = createSelector(
  selectCreditState,
  (state) => state?.towerControls,
)

export const selectActualsAsOfDate = createSelector(
  selectCurrentPortfolio,
  (state) => state?.actuals?.slice(0, 1).shift()?.as_of_date ?? '',
)

export const selectGsePortfolioIds = createSelector(
  selectCreditState,
  (state) => state.gsePortfolioIds,
)

export const selectGsePortfolioIdsLoaded = createSelector(
  selectCreditState,
  (state) => state.gsePortfolioIdsLoaded,
)

// Note: these two selectors deliberately do NOT gate on `selectGsePortfolioIdsLoaded`.
// Some roles (e.g. Reinsurer) can never successfully load the GSE portfolio ID list — see
// loadGsePortfolioIds$ / GsePortfolioController.GetGsePortfolios(). Blocking client lists
// until that fetch succeeds means those roles never see any clients at all. Instead, these
// compute best-effort from whatever `gsePortfolioIds` currently holds (`[]` until/unless the
// fetch succeeds), so the lists populate immediately and simply narrow correctly if/when the
// fetch does succeed.
export const selectGSECreditClients = createSelector(
  selectClientsState,
  selectGsePortfolioIds,
  (clientsState, gsePortfolioIds) => {
    const gseSet = new Set(gsePortfolioIds)
    return Object.values(clientsState.entities).filter(
      (client): client is Client =>
        !!client?.clientYears?.some((year) =>
          year.studies?.some(
            (study) =>
              !!study.credit_sage_portfolio_id && gseSet.has(study.credit_sage_portfolio_id),
          ),
        ),
    )
  },
)

export const selectMICreditClients = createSelector(
  selectClientsState,
  selectGsePortfolioIds,
  (clientsState, gsePortfolioIds) => {
    const gseSet = new Set(gsePortfolioIds)
    return Object.values(clientsState.entities).filter((client): client is Client => {
      if (!client || !hasCreditPortfolio(client)) return false
      return !client.clientYears?.some((year) =>
        year.studies?.some(
          (study) => !!study.credit_sage_portfolio_id && gseSet.has(study.credit_sage_portfolio_id),
        ),
      )
    })
  },
)
